import { AxiosResponse } from "axios";
import api from "./api";
import type {
  IEventoVendaFoto,
  IFotografoCompartilhadoRepasse,
  IListagemFotosEventoGerenciar,
  IUploadFotosEventoResult,
  IRelatorioRepasseFotos,
  IUpsertVendaFotoPayload,
  IFotoEventoGerenciar,
} from "interfaces/eventoFoto";

function ensureSuccess<T>(response: AxiosResponse<T>): T {
  if (response.status >= 400) {
    const data = response.data as { error?: string; message?: string };
    throw new Error(
      data?.error || data?.message || "Falha na comunicação com o servidor"
    );
  }
  return response.data;
}

function normalizeError(error: unknown) {
  const err = error as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return new Error(
    err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Falha na comunicação com o servidor"
  );
}

export async function listarFotosEventoGerenciar(
  eventoId: number,
  params?: { page?: number; limit?: number; ativo?: boolean }
): Promise<IListagemFotosEventoGerenciar> {
  try {
    const response = await api.get<IListagemFotosEventoGerenciar>(
      `/eventos/${eventoId}/fotos/gerenciar`,
      { params }
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function uploadFotosEvento(
  eventoId: number,
  arquivos: File[]
): Promise<IUploadFotosEventoResult> {
  const formData = new FormData();
  arquivos.forEach((file) => {
    formData.append("files", file, file.name);
  });

  try {
    const response = await api.post<IUploadFotosEventoResult>(
      `/eventos/${eventoId}/fotos/upload`,
      formData
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function atualizarFotoEvento(
  eventoId: number,
  fotoId: string,
  body: {
    ativa?: boolean;
    fotografo?: string | null;
    percentualRepasseFotografo?: number;
  }
): Promise<IFotoEventoGerenciar> {
  try {
    const response = await api.put<IFotoEventoGerenciar>(
      `/eventos/${eventoId}/fotos/${fotoId}`,
      body
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterRelatorioRepasseFotos(
  eventoId: number
): Promise<IRelatorioRepasseFotos> {
  try {
    const response = await api.get<IRelatorioRepasseFotos>(
      `/eventos/${eventoId}/fotos/relatorio-repasses`
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function listarFotografosCompartilhadosEvento(
  eventoId: number
): Promise<IFotografoCompartilhadoRepasse[]> {
  try {
    const response = await api.get<IFotografoCompartilhadoRepasse[]>(
      `/eventos/${eventoId}/fotos/fotografos-compartilhados`
    );
    const data = ensureSuccess(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function salvarRepassesFotografosEvento(
  eventoId: number,
  repasses: Array<{
    compartilhamentoId: number;
    percentualRepasseFotografo: number;
  }>
): Promise<IFotografoCompartilhadoRepasse[]> {
  try {
    const response = await api.put<IFotografoCompartilhadoRepasse[]>(
      `/eventos/${eventoId}/fotos/repasses-fotografos`,
      { repasses }
    );
    const data = ensureSuccess(response);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function excluirFotoEvento(
  eventoId: number,
  fotoId: string
): Promise<{ ok: boolean; id: string }> {
  try {
    const response = await api.delete<{ ok: boolean; id: string }>(
      `/eventos/${eventoId}/fotos/${fotoId}`
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function obterVendaFotoEvento(
  eventoId: number
): Promise<IEventoVendaFoto | null> {
  try {
    const response = await api.get<IEventoVendaFoto | null>(
      `/eventos/${eventoId}/venda-foto`
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}

export async function upsertVendaFotoEvento(
  eventoId: number,
  body: IUpsertVendaFotoPayload
): Promise<IEventoVendaFoto> {
  try {
    const response = await api.put<IEventoVendaFoto>(
      `/eventos/${eventoId}/venda-foto`,
      body
    );
    return ensureSuccess(response);
  } catch (error) {
    throw normalizeError(error);
  }
}
